import type { FC, ReactNode } from 'react'
import type { Amenity, Area, Style } from '@/types'

import { useMemo } from 'react'
import { Check, Map as MapIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCompactPrice, formatPrice } from '@/lib/format'
import { getIcon, getTypeIcon } from '@/lib/icons'
import { Photo } from '@/components/generals/photo'
import { Slider } from '@/components/ui/slider'

/* Where: area photo tiles */

interface AreaPickerProps {
    areas: Area[]
    value: string
    onChange: (slug: string) => void
    columns?: string
}

export const AreaPicker: FC<AreaPickerProps> = ({ areas, value, onChange, columns = 'grid-cols-3' }): ReactNode => (
    <div className={cn('grid gap-3', columns)} role='radiogroup' aria-label='Choose an area'>
        <button
            type='button'
            role='radio'
            aria-checked={value === ''}
            onClick={() => onChange('')}
            className='group text-left focus-visible:outline-none'
        >
            <span className={cn(
                'relative flex aspect-square items-center justify-center overflow-hidden rounded-xl border bg-brand-soft text-primary transition group-hover:border-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring',
                value === '' ? 'border-foreground ring-1 ring-foreground' : 'border-border'
            )}>
                <MapIcon className='size-8' aria-hidden='true' />
            </span>
            <span className='mt-1.5 block truncate text-sm font-medium'>Anywhere</span>
        </button>

        {areas.map(area => (
            <button
                key={area.id}
                type='button'
                role='radio'
                aria-checked={value === area.slug}
                onClick={() => onChange(area.slug)}
                className='group text-left focus-visible:outline-none'
            >
                <span className={cn(
                    'relative block aspect-square overflow-hidden rounded-xl border transition group-hover:border-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring',
                    value === area.slug ? 'border-foreground ring-1 ring-foreground' : 'border-border'
                )}>
                    <Photo src={area.photo} alt={area.photo_alt} sizes='140px' />
                </span>
                <span className='mt-1.5 block truncate text-sm font-medium'>
                    <span data-mdlfy-cms={`areas:${area.id}:name`}>{area.name}</span>
                </span>
            </button>
        ))}
    </div>
)

/* Home type: icon cards */

interface TypePickerProps {
    types: string[]
    value: string[]
    onChange: (types: string[]) => void
}

export const TypePicker: FC<TypePickerProps> = ({ types, value, onChange }): ReactNode => (
    <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
        {types.map(type => {
            const key = type.toLowerCase()
            const selected = value.includes(key)
            const Icon = getTypeIcon(type)

            return (
                <button
                    key={type}
                    type='button'
                    aria-pressed={selected}
                    onClick={() => onChange(selected ? value.filter(item => item !== key) : [...value, key])}
                    className={cn(
                        'flex flex-col items-start gap-6 rounded-xl border p-4 text-left text-sm font-semibold transition hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected ? 'border-foreground bg-secondary ring-1 ring-foreground' : 'border-border'
                    )}
                >
                    <Icon className='size-7' strokeWidth={1.5} aria-hidden='true' />
                    {type}
                </button>
            )
        })}
    </div>
)

/* Price: histogram with a two-handle slider */

interface PriceRangeProps {
    prices: number[]
    minPrice: number | null
    maxPrice: number | null
    onChange: (min: number | null, max: number | null) => void
}

const PRICE_STEP = 50_000
const BUCKETS = 24

export const usePriceBounds = (prices: number[]): [number, number] => useMemo(() => {
    if (!prices.length) return [0, 1_000_000]

    const low = Math.floor(Math.min(...prices) / 100_000) * 100_000
    const high = Math.ceil(Math.max(...prices) / 100_000) * 100_000

    return [low, high > low ? high : low + 100_000]
}, [prices])

export const PriceRange: FC<PriceRangeProps> = ({ prices, minPrice, maxPrice, onChange }): ReactNode => {
    const [low, high] = usePriceBounds(prices)
    const current: [number, number] = [
        Math.max(low, Math.min(minPrice ?? low, high)),
        Math.min(high, Math.max(maxPrice ?? high, low))
    ]

    const buckets = useMemo(() => {
        const counts = Array.from({ length: BUCKETS }, () => 0)
        const width = (high - low) / BUCKETS

        for (const price of prices) {
            const index = Math.min(BUCKETS - 1, Math.max(0, Math.floor((price - low) / width)))
            counts[index] += 1
        }

        const peak = Math.max(1, ...counts)

        return counts.map((count, index) => ({
            height: count ? 18 + (count / peak) * 82 : 6,
            start: low + index * width,
            end: low + (index + 1) * width
        }))
    }, [prices, low, high])

    const handleChange = (value: number | readonly number[]) => {
        const [nextMin, nextMax] = Array.isArray(value) ? value : [value, high]

        onChange(nextMin <= low ? null : nextMin, nextMax >= high ? null : nextMax)
    }

    return (
        <div>
            <div className='flex h-20 items-end gap-0.5 px-3.5' aria-hidden='true'>
                {buckets.map(bucket => (
                    <div
                        key={bucket.start}
                        className={cn(
                            'flex-1 rounded-t-sm transition-colors',
                            bucket.end > current[0] && bucket.start < current[1] ? 'bg-primary' : 'bg-border'
                        )}
                        style={{ height: `${bucket.height}%` }}
                    />
                ))}
            </div>

            <div className='-mt-3 px-0.5'>
                <Slider
                    value={current}
                    min={low}
                    max={high}
                    step={PRICE_STEP}
                    minStepsBetweenValues={1}
                    onValueChange={handleChange}
                    thumbLabels={['Minimum price', 'Maximum price']}
                    formatValueText={formatPrice}
                />
            </div>

            <div className='mt-3 grid grid-cols-2 gap-4'>
                <div className='rounded-xl border border-input px-4 py-2'>
                    <span className='block text-xs text-muted-foreground'>Minimum</span>
                    <span className='text-base font-medium'>{formatPrice(current[0])}</span>
                </div>

                <div className='rounded-xl border border-input px-4 py-2 text-right'>
                    <span className='block text-xs text-muted-foreground'>Maximum</span>
                    <span className='text-base font-medium'>
                        {formatPrice(current[1])}
                        {current[1] >= high && '+'}
                    </span>
                </div>
            </div>
        </div>
    )
}

export const describePrice = (minPrice: number | null, maxPrice: number | null): string => {
    if (minPrice && maxPrice) return `${formatCompactPrice(minPrice)} to ${formatCompactPrice(maxPrice)}`
    if (minPrice) return `${formatCompactPrice(minPrice)}+`
    if (maxPrice) return `Up to ${formatCompactPrice(maxPrice)}`

    return ''
}

/* Bedrooms and bathrooms: pills */

interface CountPillsProps {
    label: string
    value: number
    onChange: (value: number) => void
}

export const CountPills: FC<CountPillsProps> = ({ label, value, onChange }): ReactNode => (
    <div role='radiogroup' aria-label={label} className='flex flex-wrap gap-2'>
        {[0, 1, 2, 3, 4, 5].map(count => (
            <button
                key={count}
                type='button'
                role='radio'
                aria-checked={value === count}
                onClick={() => onChange(count)}
                className={cn(
                    'h-10 min-w-14 rounded-full border px-4 text-sm font-medium transition hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    value === count ? 'border-foreground bg-foreground text-background' : 'border-border'
                )}
            >
                {count === 0 ? 'Any' : `${count}+`}
            </button>
        ))}
    </div>
)

/* Styles: photo cards */

interface StylePickerProps {
    styles: Style[]
    value: string[]
    onChange: (styles: string[]) => void
}

export const StylePicker: FC<StylePickerProps> = ({ styles, value, onChange }): ReactNode => (
    <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
        {styles.map(style => {
            const selected = value.includes(style.slug)

            return (
                <button
                    key={style.id}
                    type='button'
                    aria-pressed={selected}
                    onClick={() => onChange(selected ? value.filter(item => item !== style.slug) : [...value, style.slug])}
                    className='group text-left focus-visible:outline-none'
                >
                    <span className={cn(
                        'relative block aspect-4/3 overflow-hidden rounded-xl border transition group-hover:border-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring',
                        selected ? 'border-foreground ring-2 ring-foreground' : 'border-border'
                    )}>
                        <Photo src={style.photo} alt={style.photo_alt} sizes='200px' />

                        {selected && (
                            <span className='absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-foreground text-background'>
                                <Check className='size-4' aria-hidden='true' />
                            </span>
                        )}
                    </span>
                    <span className='mt-1.5 block truncate text-sm font-medium'>
                        <span data-mdlfy-cms={`styles:${style.id}:name`}>{style.name}</span>
                    </span>
                </button>
            )
        })}
    </div>
)

/* Amenities: icon chips */

interface AmenityPickerProps {
    amenities: Amenity[]
    value: string[]
    onChange: (amenities: string[]) => void
}

export const AmenityPicker: FC<AmenityPickerProps> = ({ amenities, value, onChange }): ReactNode => (
    <div className='flex flex-wrap gap-2'>
        {amenities.map(amenity => {
            const selected = value.includes(amenity.slug)
            const Icon = getIcon(amenity.icon)

            return (
                <button
                    key={amenity.id}
                    type='button'
                    aria-pressed={selected}
                    onClick={() => onChange(selected ? value.filter(item => item !== amenity.slug) : [...value, amenity.slug])}
                    className={cn(
                        'flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected ? 'border-foreground bg-secondary ring-1 ring-foreground' : 'border-border'
                    )}
                >
                    <Icon className='size-5' strokeWidth={1.5} aria-hidden='true' />
                    <span data-mdlfy-cms={`amenities:${amenity.id}:name`}>{amenity.name}</span>
                </button>
            )
        })}
    </div>
)
